import React, { Fragment, useState } from 'react';
import {
  AppModal,
  AppNotificationAlert,
  AppRemixIcon,
  AppTextArea,
} from '@/layout/AppComponents';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Scrollbars } from 'react-custom-scrollbars';
import ReactDOMServer from 'react-dom/server';
import colors from '../../../../../../../constants/colors';
import { axiosPath } from '../../../../../../../routing/helpers/router';
import plantObligationStates from '../../../../../../../constants/plantObligationStates';
import Translator from '../../../../../../components/helpers/Translator';
import { Formik } from 'formik';
import SpinnerLoader from '@/layout/components/helpers/SpinnerLoader';
import errorHandler from '../../../../../../components/error/ErrorHandler';

const StateHistory = (props) => {
  const { state, plantObligationID } = props;
  const dispatch = useDispatch();
  const { moduleBridgeHex, plantHex } = useParams();
  const customStates = useSelector((s) => s.auth?.customStates);
  const permissions = useSelector((s) => s.module[moduleBridgeHex]?.permissions);
  const poLevel3 = useSelector(
    (s) => s.currentPage?.plantObligationLevels?.level3?.[plantObligationID],
  );
  const [showHistory, setShowHistory] = useState(false);
  const [changes, setChanges] = useState(null);
  const [rowSelectedCommentModal, setRowSelectedCommentModal] = useState(null);

  // > LOAD CHANGES
  const loadModuleList = async () => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_legalCompliance_evaluation_getStateHistory'),
        { plantObligationID, plantHex, moduleBridgeHex },
      );
      setChanges(response.data);
      setShowHistory(true);
    } catch (err) {
      dispatch(errorHandler(err, history));
    }
  };
  // < LOAD CHANGES

  // > EDIT COMMENT
  const editModuleList = async (values) => {
    await axios
      .post(
        axiosPath('api_r_user_module_legalCompliance_evaluation_editStateHistory'),
        { plantObligationID, plantHex, moduleBridgeHex, ...values },
      )
      .then(() =>
        AppNotificationAlert({
          title: 'Completado',
          message: 'Se han guardado los cambios corréctamente',
          type: 'success',
        }),
      )
      .catch((err) => dispatch(errorHandler(err, history)))
      .finally(() => loadModuleList());
  };
  // < EDIT COMMENT

  return (
    <>
      <AppRemixIcon
        onClick={() => loadModuleList()}
        icon="calendar-check"
        title={ReactDOMServer.renderToString(
          <Translator file="module-legalCompliance" name="see.state.history" />,
        )}
        color={poLevel3?.alerts?.stateHistory ? colors.primaryLight : colors.grey300}
        filled={state}
        className="pointer"
      />
      <AppModal
        show={showHistory}
        onClose={() => setShowHistory(false)}
        title={ReactDOMServer.renderToString(
          <Translator file="module-legalCompliance" name="state.history" />,
        )}
      >
        <Scrollbars style={{ height: '380px' }}>
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th scope="col">
                  <Translator file="module-legalCompliance" name="date" />
                </th>
                <th scope="col">
                  <Translator file="user" name="user.state" />
                </th>
                {permissions?.gcl?.module?.plant?.evidences?.showUserName && (
                  <th scope="col">
                    <Translator file="general" name="general.user" />
                  </th>
                )}
                {permissions?.gcl?.module?.plant?.reports?.xls9 !== null && (
                  <th className="text-center pr-4" scope="col">
                    <Translator file="module-legalCompliance" name="comment" />
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {changes &&
                changes.map((c) => (
                  <Fragment key={c.id}>
                    <tr>
                      <td scope="row">{c.dateStamp}</td>
                      <td>
                        {c.state !== 6 ? (
                          <b
                            className="p-1"
                            style={{
                              background: plantObligationStates[c.state].bgColor,
                              color: plantObligationStates[c.state].iconColor,
                              borderRadius: '2px',
                            }}
                          >
                            <Translator
                              file="user"
                              name={`user.obligations.state${
                                plantObligationStates[c.state].id
                              }`}
                            />
                          </b>
                        ) : (
                          <>
                            {customStates[c.customState] ? (
                              <b
                                className="p-1"
                                style={{
                                  background: customStates[c.customState].bgColor,
                                  color: customStates[c.customState].fgColor,
                                  borderRadius: '2px',
                                }}
                              >
                                {customStates[c.customState].name}
                              </b>
                            ) : (
                              <span>
                                <Translator
                                  file="module-legalCompliance"
                                  name="legalCompliance-custom.status"
                                />
                              </span>
                            )}
                          </>
                        )}
                      </td>
                      {permissions?.gcl?.module?.plant?.evidences?.showUserName && (
                        <td>{c.user}</td>
                      )}
                      {permissions?.gcl?.module?.plant?.reports?.xls9 && (
                        <td className="text-center pr-4">
                          <span
                            onClick={() =>
                              rowSelectedCommentModal === c.id
                                ? setRowSelectedCommentModal(null)
                                : setRowSelectedCommentModal(c.id)
                            }
                          >
                            <AppRemixIcon
                              icon="eye"
                              size={20}
                              filled={
                                rowSelectedCommentModal === c.id ? 'true' : false
                              }
                            />
                          </span>
                        </td>
                      )}
                    </tr>
                    {rowSelectedCommentModal === c.id && (
                      <tr>
                        <td
                          colSpan={
                            permissions?.gcl?.module?.plant?.evidences?.showUserName
                              ? '4'
                              : '3'
                          }
                        >
                          <Formik
                            initialValues={{
                              obligationChangeID: c.id,
                              comment: c.comment ?? '',
                            }}
                            onSubmit={editModuleList}
                          >
                            {({
                              setFieldValue,
                              handleSubmit,
                              values,
                              isSubmitting,
                            }) => (
                              <div>
                                <AppTextArea
                                  title={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-legalCompliance"
                                      name="comment"
                                    />,
                                  )}
                                  value={values.comment}
                                  height="120px"
                                  onChange={(v) => setFieldValue('comment', v)}
                                />
                                <div className="d-flex flex justify-content-end my-3 align-items-center">
                                  {isSubmitting && (
                                    <div className="mr-2">
                                      <SpinnerLoader size={20} />
                                    </div>
                                  )}
                                  <button
                                    onClick={handleSubmit}
                                    className={`btn btn-outline-${isSubmitting ? 'secondary' : 'primary'}`}
                                    type="submit"
                                    disabled={isSubmitting}
                                  >
                                    <Translator
                                      file="module-legalCompliance"
                                      name="save"
                                    />
                                  </button>
                                </div>
                              </div>
                            )}
                          </Formik>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
            </tbody>
          </table>
        </Scrollbars>
      </AppModal>
    </>
  );
};
export default StateHistory;
